import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMountedState } from 'react-use';
import { v4 } from 'uuid';
import {
  initCommission,
  goToNextCommissionStep,
  terminateCommission,
  GA2CommissioningSteps as Steps,
  GA2CommissioningStepStatuses as Statuses
} from 'redux/Commissioning';
import Button from 'components/shared/Button';
import Dialog from 'components/shared/Dialog';
import Stepper from 'components/shared/Stepper';
import {
  BeamSettingsForm,
  AcquisitionInfoForm,
  AcquisitionStatusProgress,
  BeamSelectionForm,
  InServiceForm,
  CommissionStep,
  CommissionPanel
} from './components';
import { ProgressIndicator } from 'components/shared/Progress';
import { getGa2DeviceMembers } from 'common/utils/deviceTwinUtils';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import styles from './CommissionGA2Modal.module.css';
import Modal from 'components/shared/Modal/Modal';
import AcquisitionRasterCancelWarning from 'components/features/HeatmapDiagRaster/AcquisitionRasterCancelWarning/AcquisitionRasterCancelWarning';

const steps = [
  {
    value: Steps.BEAM_SETTINGS,
    label: 'Beam Settings',
    render: (props) => <BeamSettingsForm {...props} />
  },
  {
    value: Steps.ACQUISITION_INFO,
    label: 'Acquisition Info',
    render: (props) => <AcquisitionInfoForm {...props} />
  },
  {
    value: Steps.ACQUISITION_STATUS,
    label: 'Acquisition Status',
    render: (props) => <AcquisitionStatusProgress {...props} />
  },
  {
    value: Steps.BEAM_SELECTION,
    label: 'Beam Selection',
    render: (props) => <BeamSelectionForm {...props} />
  },
  {
    value: Steps.IN_SERVICE,
    label: 'In Service',
    render: (props) => <InServiceForm {...props} />
  }
];

function CommissionGA2Modal({ deviceId, onClose }) {
  const isMounted = useMountedState();
  const dispatch = useDispatch();
  const [titleId] = useState(() => v4());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const targetDonor = useSelector((state) => state.ui.pivotOverviewPage.selectedDevices[deviceId]);
  const error = useSelector((state) => state.data.commissioningGA2.error);
  const isLoaded = useSelector((state) => state.data.commissioningGA2.deviceData != null);
  const currentStep = useSelector((state) => state.data.commissioningGA2.currentStep);
  const currentStepStatus = useSelector((state) => state.data.commissioningGA2.stepStatuses[currentStep]);
  const nextStepIndex = steps.findIndex((step) => step.value === currentStep) + 1;
  const rasterStatus = useSelector((state) => state.data.commissioningGA2.rasterStatus);

  const allMemberIds = useMemo(() => {
    const deviceMembers = getGa2DeviceMembers(targetDonor);
    return [deviceId, ...deviceMembers.map(({ id }) => id)];
  }, [deviceId, targetDonor]);

  const isCurrentStepCompleted = () => {
    if (currentStep === Steps.BEAM_SELECTION) return currentStepStatus === Statuses.SUCCESS;

    return [Statuses.PRISTINE, Statuses.SUCCESS].includes(currentStepStatus);
  };

  const handleNext = useCallback(() => {
    setIsTransitioning(true);

    dispatch(goToNextCommissionStep()).then(() => {
      if (isMounted()) {
        if (nextStepIndex % steps.length === 0) {
          onClose();
        } else {
          setIsTransitioning(false);
        }
      }
    });
  }, [dispatch, onClose, isMounted, nextStepIndex]);
  const handleDialogClose = useCallback(
    (event) => {
      if (currentStep === Steps.ACQUISITION_STATUS && rasterStatus && rasterStatus?.customStatus?.status !== 'completed') {
        setShowCancelWarning(true);
      } else {
        onClose();
        dispatch(terminateCommission());
      }
    },
    [dispatch, onClose, currentStep, rasterStatus]
  );

  useEffect(() => {
    dispatch(initCommission(deviceId));
  }, [dispatch, deviceId]);

  return (
    <>
      <Dialog aria-labelledby={titleId} open={true} onClose={handleDialogClose}>
        <Dialog.Container maxWidth={1120} maxHeight={900}>
          {isTransitioning && <ProgressIndicator />}
          <Dialog.Title id={titleId} className={styles.titleContainer}>
            <h3 className={styles.title}>Commissioning</h3>
          </Dialog.Title>
          <div className={styles.deviceIdsContainer}>
            {allMemberIds.map((memberId) => (
              <div className={styles.deviceIdContainer} key={memberId}>
                <SvgAF img={memberId === deviceId ? IconLoader.icon_donor : IconLoader.icon_service} />
                <span className={styles.deviceId}>{memberId}</span>
              </div>
            ))}
          </div>
          <Stepper
            value={currentStep}
            steps={steps}
            stepComponent={CommissionStep}
            panelComponent={CommissionPanel}
            getPanelProps={({ value }) => ({ value, error, loading: !isLoaded })}
          />
          <Dialog.Actions>
            {nextStepIndex % steps.length === 0 && (
              <Fragment>
                <div className={styles.note}>
                  <strong>Please note:</strong> The activation of each Sector will require a maximum of 10 minutes.
                </div>
                <Button variant="link" size="medium" color="primary" onClick={handleDialogClose}>
                  Cancel
                </Button>
              </Fragment>
            )}
            <Button
              variant="contained"
              size="medium"
              color="primary"
              disabled={!isLoaded || isTransitioning || !isCurrentStepCompleted()}
              onClick={handleNext}
            >
              {nextStepIndex % steps.length > 0 ? `Next: ${steps[nextStepIndex].label}` : 'Put Pivot in Service'}
            </Button>
          </Dialog.Actions>
          <Dialog.Close onClick={handleDialogClose} />
        </Dialog.Container>
      </Dialog>

      {showCancelWarning && (
        <Modal close={() => setShowCancelWarning(false)} height={238} width={454} zIndex={10000}>
          <AcquisitionRasterCancelWarning
            title="Acquisition"
            rasterStatus={rasterStatus?.customStatus?.status === 'completed' ? 'completed' : 'in_progress'}
            onCancel={() => {
              setShowCancelWarning(false);
            }}
            onProceed={() => {
              setShowCancelWarning(false);
              onClose();
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default CommissionGA2Modal;
